import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import { openModalAction } from '../../../actions/modal';
import { actionsAdvancedFilters } from '../../../components/AdvancedFilters/actions';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import { PageHeader } from '../../../components/PageHeader';
import { Button } from '../../../components/PageHeader/Button';
import { TextInput } from '../../../components/PageHeader/TextInput';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { HEADER_BUTTON_BLUE } from '../../../constants';
import { CREATE_ISSUE_MODAL_NAME } from '../../../modals/CreateIssueModal';
import Strings from '../../../Strings';
import actions from './redux/actions';
import {
  buildCheckboxFilterDefinition,
  formatIssueDate,
  getAdvancedFilterValues,
  getAssignableUserLabel,
  getResponseItems,
  isUnassignedIssue,
  ISSUE_SEVERITY_OPTIONS,
  NONE_ASSIGNEE_FILTER,
  ORGLESS_SUPERUSER_ROLE_TITLE,
} from './shared';

import './MessagesOverview.scss';
import './IssuesOverview.scss';

const ISSUE_FILTER_GROUPS = {
  status: 'healthIssueStatus',
  severity: 'healthIssueSeverity',
  category: 'healthIssueCategory',
  assignee: 'healthIssueAssignee',
};

const ISSUE_CATEGORY_OPTIONS = [
  { value: 'Auto Detected', label: 'Auto Detected' },
];

const STATUS_OPTIONS = [
  { value: 'false', label: Strings.open },
  { value: 'true', label: Strings.resolved },
];

function IssueSeverityBadge({ severity }) {
  const resolvedSeverity = severity || 'low';
  return <span className={`health-issues__badge is-${resolvedSeverity}`}>{resolvedSeverity}</span>;
}

IssueSeverityBadge.propTypes = {
  severity: PropTypes.string,
};

function renderIssueTextCell(info) {
  return info.getValue() || '-';
}

function renderIssueDateCell(info) {
  return formatIssueDate(info.getValue());
}

function renderIssueSeverityCell(info) {
  return <IssueSeverityBadge severity={info.getValue()} />;
}

function renderIssueStatusCell(info) {
  return info.row.original.resolved_at ? Strings.resolved : Strings.open;
}

function buildIssueFilterDefinitions(assignableUsers) {
  return {
    ...buildCheckboxFilterDefinition(ISSUE_FILTER_GROUPS.status, Strings.status, STATUS_OPTIONS),
    ...buildCheckboxFilterDefinition(ISSUE_FILTER_GROUPS.severity, Strings.severity, ISSUE_SEVERITY_OPTIONS.slice(1)),
    ...buildCheckboxFilterDefinition(ISSUE_FILTER_GROUPS.category, Strings.category, ISSUE_CATEGORY_OPTIONS),
    ...buildCheckboxFilterDefinition(ISSUE_FILTER_GROUPS.assignee, Strings.assignedTo, [
      { value: NONE_ASSIGNEE_FILTER, label: Strings.none },
      ...assignableUsers.map(user => ({
        value: user.username || '',
        label: getAssignableUserLabel(user),
      })),
    ]),
  };
}

function getFilteredIssues(issues, filters, searchQuery) {
  const statusValues = getAdvancedFilterValues(filters, ISSUE_FILTER_GROUPS.status);
  const severityValues = getAdvancedFilterValues(filters, ISSUE_FILTER_GROUPS.severity);
  const categoryValues = getAdvancedFilterValues(filters, ISSUE_FILTER_GROUPS.category);
  const assigneeValues = getAdvancedFilterValues(filters, ISSUE_FILTER_GROUPS.assignee);
  const normalizedSearch = searchQuery.trim().toLowerCase();

  return issues.filter(issue => {
    if (statusValues.length === 1) {
      const isResolved = Boolean(issue.resolved_at);
      if (statusValues[0] === 'true' && !isResolved) {
        return false;
      }
      if (statusValues[0] === 'false' && isResolved) {
        return false;
      }
    }

    if (severityValues.length > 0 && !severityValues.includes(issue.severity || 'low')) {
      return false;
    }

    if (categoryValues.length > 0 && !categoryValues.includes(issue.category || '')) {
      return false;
    }

    if (assigneeValues.length > 0) {
      const hasNone = assigneeValues.includes(NONE_ASSIGNEE_FILTER);
      const usernameValues = assigneeValues.filter(value => value !== NONE_ASSIGNEE_FILTER);
      const isUnassigned = isUnassignedIssue(issue);

      if (usernameValues.length > 0 || hasNone) {
        const matchesAssignedUser = usernameValues.includes(issue.assigned_to || '');
        if (!(matchesAssignedUser || (hasNone && isUnassigned))) {
          return false;
        }
      }
    }

    if (!normalizedSearch) {
      return true;
    }

    const haystack = [
      issue.issue_id,
      issue.title,
      issue.device_id,
      issue.category,
      issue.created_by,
      issue.assigned_to,
      issue.org_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return haystack.includes(normalizedSearch);
  });
}

function IssuesOverview({
  clearAllAdvancedFilters,
  filters,
  getAdminRoles,
  getAdminUsers,
  getIssues,
  openCreateIssueModal,
  onNavigate,
  updateAdvancedFilterState,
}) {
  const [issues, setIssues] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [assignableUsers, setAssignableUsers] = React.useState([]);
  const [assignableUsersError, setAssignableUsersError] = React.useState('');

  const loadIssues = React.useCallback(() => {
    setIsLoading(true);
    setError('');

    getIssues({ limit: 1000, sortColumn: 'created_at', sortType: 'descending' })
      .then(resp => {
        setIssues(Array.isArray(resp?.response) ? resp.response : []);
        setIsLoading(false);
      })
      .catch(() => {
        setError(Strings.superuserHealthLoadIssuesError);
        setIsLoading(false);
      });
  }, [getIssues]);

  React.useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  React.useEffect(() => {
    let isMounted = true;

    const loadAssignableUsers = async () => {
      try {
        const rolesResp = await getAdminRoles({
          limit: 200,
          search: ORGLESS_SUPERUSER_ROLE_TITLE,
          sortColumn: 'title',
          sortType: 'ascending',
        });
        const role = getResponseItems(rolesResp).find(item => item?.title === ORGLESS_SUPERUSER_ROLE_TITLE);

        if (!role?.role_uuid) {
          if (isMounted) {
            setAssignableUsersError(Strings.superuserHealthLoadAssigneeRoleError);
          }
          return;
        }

        const usersResp = await getAdminUsers({
          limit: 200,
          role: role.role_uuid,
          sortColumn: 'first_name',
          sortType: 'ascending',
        });

        if (isMounted) {
          setAssignableUsers(getResponseItems(usersResp));
          setAssignableUsersError('');
        }
      } catch (loadError) {
        if (isMounted) {
          setAssignableUsers([]);
          setAssignableUsersError(Strings.superuserHealthLoadAssignableUsersError);
        }
      }
    };

    loadAssignableUsers();

    return () => {
      isMounted = false;
    };
  }, [getAdminRoles, getAdminUsers]);

  const filterDefinitions = React.useMemo(() => buildIssueFilterDefinitions(assignableUsers), [assignableUsers]);
  const filteredIssues = React.useMemo(
    () => getFilteredIssues(issues, filters, searchQuery),
    [filters, issues, searchQuery],
  );

  const onSearchChangeDebounced = React.useMemo(
    () => _.debounce(query => setSearchQuery(query), 250),
    [],
  );

  React.useEffect(() => {
    return () => {
      onSearchChangeDebounced.cancel();
    };
  }, [onSearchChangeDebounced]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resolved = params.get('resolved');
    const assignedTo = params.get('assignedTo');

    if (!resolved && !assignedTo) {
      return;
    }

    clearAllAdvancedFilters();

    if (resolved === 'true' || resolved === 'false') {
      updateAdvancedFilterState({
        group: ISSUE_FILTER_GROUPS.status,
        value: resolved,
        state: true,
      });
    }

    if (assignedTo) {
      updateAdvancedFilterState({
        group: ISSUE_FILTER_GROUPS.assignee,
        value: assignedTo,
        state: true,
      });
    }
  }, [clearAllAdvancedFilters, updateAdvancedFilterState]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('issue_id', {
        header: 'ID',
        enableSorting: true,
      }),
      columnHelper.accessor('title', {
        header: Strings.title,
        enableSorting: true,
        size: 260,
        cell: renderIssueTextCell,
      }),
      columnHelper.accessor('device_id', {
        header: Strings.deviceId,
        cell: renderIssueTextCell,
      }),
      columnHelper.accessor('created_at', {
        header: Strings.createdAt,
        enableSorting: true,
        cell: renderIssueDateCell,
      }),
      columnHelper.accessor('created_by', {
        header: Strings.createdBy,
        cell: renderIssueTextCell,
      }),
      columnHelper.accessor('category', {
        header: Strings.category,
        cell: renderIssueTextCell,
      }),
      columnHelper.accessor('severity', {
        header: Strings.severity,
        cell: renderIssueSeverityCell,
      }),
      columnHelper.accessor('status', {
        header: Strings.status,
        cell: renderIssueStatusCell,
      }),
      columnHelper.accessor('assigned_to', {
        header: Strings.assignedTo,
        cell: renderIssueTextCell,
      }),
      columnHelper.accessor('org_name', {
        header: Strings.organization,
        cell: renderIssueTextCell,
      }),
      columnHelper.accessor('resolved_at', {
        header: Strings.superuserHealthResolvedAt,
        cell: renderIssueDateCell,
      }),
    ],
    [],
  );

  return (
    <>
      <PageHeader
        left={Strings.superuserHealthIssuesTitle}
        right={(
          <>
            <TextInput
              class="search"
              placeholder={Strings.superuserHealthSearchIssues}
              onChange={event => onSearchChangeDebounced(event.target.value)}
            />
            <AdvancedFilters>
              <Filter definition={filterDefinitions[ISSUE_FILTER_GROUPS.status]} />
              <Filter definition={filterDefinitions[ISSUE_FILTER_GROUPS.severity]} />
              <Filter definition={filterDefinitions[ISSUE_FILTER_GROUPS.category]} />
              <Filter definition={filterDefinitions[ISSUE_FILTER_GROUPS.assignee]} />
            </AdvancedFilters>
            <Button class="health-refresh-button" onClick={loadIssues} title={Strings.refresh} />
            <Button
              class={HEADER_BUTTON_BLUE}
              onClick={() =>
                openCreateIssueModal(assignableUsers, createdIssue => {
                  loadIssues();
                  if (createdIssue?.issue_id) {
                    onNavigate(`/superuser-health/issues/${createdIssue.issue_id}`);
                  }
                })}
              title={`+ ${Strings.superuserHealthCreateIssue}`}
            />
          </>
        )}
      />
      <AdvancedFiltersBar customFilters={filterDefinitions} />

      {assignableUsersError ? <div className="health-messages-error">{assignableUsersError}</div> : null}
      {error ? <div className="health-messages-error">{error}</div> : null}

      <Table3
        name="superuser-health-issues"
        data={filteredIssues}
        columns={columns}
        isLoading={isLoading}
        onRowClick={row => onNavigate(`/superuser-health/issues/${row.issue_id}`)}
      />
    </>
  );
}

IssuesOverview.propTypes = {
  clearAllAdvancedFilters: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  getAdminRoles: PropTypes.func.isRequired,
  getAdminUsers: PropTypes.func.isRequired,
  getIssues: PropTypes.func.isRequired,
  openCreateIssueModal: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  updateAdvancedFilterState: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  clearAllAdvancedFilters: () => dispatch(actionsAdvancedFilters.clearAll()),
  getAdminRoles: params => dispatch(actions.getAdminRoles(params)),
  getAdminUsers: params => dispatch(actions.getAdminUsers(params)),
  getIssues: params => dispatch(actions.getIssues(params)),
  openCreateIssueModal: (assignableUsers, onSuccess) =>
    dispatch(openModalAction(CREATE_ISSUE_MODAL_NAME, { assignableUsers, onSuccess })),
  onNavigate: path => dispatch(push(path)),
  updateAdvancedFilterState: payload => dispatch(actionsAdvancedFilters.updateFilterState(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IssuesOverview);
